import { useState, useCallback } from 'react';
import { FileSearch, CheckCircle, Copy, Check, Shield, Calendar, Fingerprint, Server, FileKey, AlertTriangle, ShieldCheck } from 'lucide-react';
import * as forge from 'node-forge';

interface CertificateDetails {
  subject: {
    CN?: string;
    O?: string;
    OU?: string;
    C?: string;
    ST?: string;
    L?: string;
  };
  issuer: {
    CN?: string;
    O?: string;
    OU?: string;
    C?: string;
  };
  validFrom: string;
  validTo: string;
  daysRemaining: number;
  serialNumber: string;
  fingerprint: string;
  fingerprint256: string;
  subjectAltNames: string[];
  keyUsage: string[];
  extKeyUsage: { name: string; oid: string }[];
  isCA: boolean;
  isSelfSigned: boolean;
  version: number;
  signatureAlgorithm: string;
  publicKeyAlgorithm: string;
  publicKeySize: number | null;
}

interface CSRDetails {
  subject: {
    CN?: string;
    O?: string;
    OU?: string;
    C?: string;
    ST?: string;
    L?: string;
  };
  publicKeyAlgorithm: string;
  publicKeySize: number | null;
  signatureAlgorithm: string;
  subjectAltNames: string[];
}

interface CertResult {
  type: 'cert';
  certificate: CertificateDetails;
  pem: string;
  timestamp: string;
}

interface CSRResult {
  type: 'csr';
  csr: CSRDetails;
  timestamp: string;
}

interface PKCS7Result {
  type: 'pkcs7';
  certificates: Array<{ details: CertificateDetails; pem: string }>;
  count: number;
  timestamp: string;
}

interface PKCS12Result {
  type: 'pkcs12';
  certificates: Array<{ details: CertificateDetails; pem: string }>;
  hasPrivateKey: boolean;
  count: number;
  timestamp: string;
}

type DecodeResult = CertResult | CSRResult | PKCS7Result | PKCS12Result;

// Helper to extract attribute from certificate name
function getAttr(attrs: forge.pki.CertificateField[], shortName: string): string | undefined {
  const attr = attrs.find(a => a.shortName === shortName);
  return attr?.value as string | undefined;
}

// Parse Subject Alt Names from certificate
function getSubjectAltNames(cert: forge.pki.Certificate): string[] {
  const sans: string[] = [];
  const ext = cert.getExtension('subjectAltName');
  if (ext && (ext as { altNames?: Array<{ type: number; value: string }> }).altNames) {
    for (const alt of (ext as { altNames: Array<{ type: number; value: string }> }).altNames) {
      if (alt.type === 2) { // DNS
        sans.push(alt.value);
      } else if (alt.type === 7) { // IP
        sans.push(alt.value);
      }
    }
  }
  return sans;
}

// Parse Key Usage from certificate
function getKeyUsage(cert: forge.pki.Certificate): string[] {
  const usages: string[] = [];
  const ext = cert.getExtension('keyUsage') as { digitalSignature?: boolean; nonRepudiation?: boolean; keyEncipherment?: boolean; dataEncipherment?: boolean; keyAgreement?: boolean; keyCertSign?: boolean; cRLSign?: boolean; encipherOnly?: boolean; decipherOnly?: boolean } | null;
  if (ext) {
    if (ext.digitalSignature) usages.push('Digital Signature');
    if (ext.nonRepudiation) usages.push('Non Repudiation');
    if (ext.keyEncipherment) usages.push('Key Encipherment');
    if (ext.dataEncipherment) usages.push('Data Encipherment');
    if (ext.keyAgreement) usages.push('Key Agreement');
    if (ext.keyCertSign) usages.push('Certificate Sign');
    if (ext.cRLSign) usages.push('CRL Sign');
  }
  return usages;
}

// EKU OID mapping
const EKU_OIDS: Record<string, string> = {
  serverAuth: '1.3.6.1.5.5.7.3.1',
  clientAuth: '1.3.6.1.5.5.7.3.2',
  codeSigning: '1.3.6.1.5.5.7.3.3',
  emailProtection: '1.3.6.1.5.5.7.3.4',
  timeStamping: '1.3.6.1.5.5.7.3.8',
  OCSPSigning: '1.3.6.1.5.5.7.3.9',
};

// Parse Extended Key Usage from certificate
function getExtKeyUsage(cert: forge.pki.Certificate): { name: string; oid: string }[] {
  const usages: { name: string; oid: string }[] = [];
  const ext = cert.getExtension('extKeyUsage') as { serverAuth?: boolean; clientAuth?: boolean; codeSigning?: boolean; emailProtection?: boolean; timeStamping?: boolean } | null;
  if (ext) {
    if (ext.serverAuth) usages.push({ name: 'Server Authentication', oid: EKU_OIDS.serverAuth });
    if (ext.clientAuth) usages.push({ name: 'Client Authentication', oid: EKU_OIDS.clientAuth });
    if (ext.codeSigning) usages.push({ name: 'Code Signing', oid: EKU_OIDS.codeSigning });
    if (ext.emailProtection) usages.push({ name: 'E-mail Protection', oid: EKU_OIDS.emailProtection });
    if (ext.timeStamping) usages.push({ name: 'Time Stamping', oid: EKU_OIDS.timeStamping });
  }
  return usages;
}

// Check if certificate is CA
function isCA(cert: forge.pki.Certificate): boolean {
  const ext = cert.getExtension('basicConstraints') as { cA?: boolean } | null;
  return ext?.cA === true;
}

// Get fingerprint in formatted hex
function getFingerprint(cert: forge.pki.Certificate, algorithm: 'sha1' | 'sha256'): string {
  const der = forge.asn1.toDer(forge.pki.certificateToAsn1(cert)).getBytes();
  const md = algorithm === 'sha1' ? forge.md.sha1.create() : forge.md.sha256.create();
  md.update(der);
  const hex = md.digest().toHex().toUpperCase();
  return hex.match(/.{2}/g)?.join(':') || hex;
}

// Get public key size
function getPublicKeySize(publicKey: forge.pki.PublicKey): number | null {
  if ('n' in publicKey) {
    return (publicKey as forge.pki.rsa.PublicKey).n.bitLength();
  }
  return null;
}

// Get signature algorithm name
function getSignatureAlgorithm(cert: forge.pki.Certificate): string {
  const oid = cert.siginfo.algorithmOid;
  const oidMap: Record<string, string> = {
    '1.2.840.113549.1.1.5': 'sha1WithRSAEncryption',
    '1.2.840.113549.1.1.11': 'sha256WithRSAEncryption',
    '1.2.840.113549.1.1.12': 'sha384WithRSAEncryption',
    '1.2.840.113549.1.1.13': 'sha512WithRSAEncryption',
    '1.2.840.10045.4.3.2': 'ecdsa-with-SHA256',
    '1.2.840.10045.4.3.3': 'ecdsa-with-SHA384',
    '1.2.840.10045.4.3.4': 'ecdsa-with-SHA512',
  };
  return oidMap[oid] || `OID: ${oid}`;
}

// Parse a single certificate and extract details
function parseCertificate(pem: string): { cert: forge.pki.Certificate; details: CertificateDetails } {
  const cert = forge.pki.certificateFromPem(pem);
  
  const validFrom = cert.validity.notBefore;
  const validTo = cert.validity.notAfter;
  const now = new Date();
  const daysRemaining = Math.floor((validTo.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  const isSelfSigned = cert.subject.hash === cert.issuer.hash;
  
  const details: CertificateDetails = {
    subject: {
      CN: getAttr(cert.subject.attributes, 'CN'),
      O: getAttr(cert.subject.attributes, 'O'),
      OU: getAttr(cert.subject.attributes, 'OU'),
      C: getAttr(cert.subject.attributes, 'C'),
      ST: getAttr(cert.subject.attributes, 'ST'),
      L: getAttr(cert.subject.attributes, 'L'),
    },
    issuer: {
      CN: getAttr(cert.issuer.attributes, 'CN'),
      O: getAttr(cert.issuer.attributes, 'O'),
      OU: getAttr(cert.issuer.attributes, 'OU'),
      C: getAttr(cert.issuer.attributes, 'C'),
    },
    validFrom: validFrom.toISOString(),
    validTo: validTo.toISOString(),
    daysRemaining,
    serialNumber: cert.serialNumber.toUpperCase(),
    fingerprint: getFingerprint(cert, 'sha1'),
    fingerprint256: getFingerprint(cert, 'sha256'),
    subjectAltNames: getSubjectAltNames(cert),
    keyUsage: getKeyUsage(cert),
    extKeyUsage: getExtKeyUsage(cert),
    isCA: isCA(cert),
    isSelfSigned,
    version: cert.version + 1,
    signatureAlgorithm: getSignatureAlgorithm(cert),
    publicKeyAlgorithm: 'n' in cert.publicKey ? 'RSA' : 'EC',
    publicKeySize: getPublicKeySize(cert.publicKey),
  };
  
  return { cert, details };
}

// Parse CSR
function parseCSR(pem: string): CSRDetails {
  const csr = forge.pki.certificationRequestFromPem(pem);
  
  const subjectAltNames: string[] = [];
  // Try to find SAN in CSR attributes/extensions
  if (csr.getAttribute && csr.getAttribute({ name: 'extensionRequest' })) {
    const extReq = csr.getAttribute({ name: 'extensionRequest' }) as { extensions?: Array<{ name: string; altNames?: Array<{ type: number; value: string }> }> };
    if (extReq?.extensions) {
      for (const ext of extReq.extensions) {
        if (ext.name === 'subjectAltName' && ext.altNames) {
          for (const alt of ext.altNames) {
            if (alt.type === 2 || alt.type === 7) {
              subjectAltNames.push(alt.value);
            }
          }
        }
      }
    }
  }
  
  let signatureAlgorithm = 'Unknown';
  const sigOid = csr.siginfo?.algorithmOid;
  if (sigOid) {
    const oidMap: Record<string, string> = {
      '1.2.840.113549.1.1.5': 'sha1WithRSAEncryption',
      '1.2.840.113549.1.1.11': 'sha256WithRSAEncryption',
      '1.2.840.113549.1.1.12': 'sha384WithRSAEncryption',
      '1.2.840.113549.1.1.13': 'sha512WithRSAEncryption',
      '1.2.840.10045.4.3.2': 'ecdsa-with-SHA256',
    };
    signatureAlgorithm = oidMap[sigOid] || `OID: ${sigOid}`;
  }
  
  let publicKeyAlgorithm = 'Unknown';
  let publicKeySize: number | null = null;
  
  if (csr.publicKey) {
    publicKeyAlgorithm = 'n' in csr.publicKey ? 'RSA' : 'EC';
    publicKeySize = getPublicKeySize(csr.publicKey);
  }
  
  return {
    subject: {
      CN: getAttr(csr.subject.attributes, 'CN'),
      O: getAttr(csr.subject.attributes, 'O'),
      OU: getAttr(csr.subject.attributes, 'OU'),
      C: getAttr(csr.subject.attributes, 'C'),
      ST: getAttr(csr.subject.attributes, 'ST'),
      L: getAttr(csr.subject.attributes, 'L'),
    },
    publicKeyAlgorithm,
    publicKeySize,
    signatureAlgorithm,
    subjectAltNames,
  };
}

// Parse PKCS#7
function parsePKCS7(pem: string): Array<{ details: CertificateDetails; pem: string }> {
  const results: Array<{ details: CertificateDetails; pem: string }> = [];
  
  let p7Data = pem.trim();
  
  // Add headers if missing
  if (!p7Data.includes('-----BEGIN')) {
    p7Data = `-----BEGIN PKCS7-----\n${p7Data}\n-----END PKCS7-----`;
  }
  
  try {
    const p7 = forge.pkcs7.messageFromPem(p7Data) as unknown as { certificates?: forge.pki.Certificate[] };
    
    // Access certificates from PKCS#7 structure
    if (p7.certificates && Array.isArray(p7.certificates)) {
      for (const cert of p7.certificates) {
        try {
          const certPem = forge.pki.certificateToPem(cert);
          const { details } = parseCertificate(certPem);
          results.push({ details, pem: certPem });
        } catch {
          // Skip invalid certificate
        }
      }
    }
  } catch {
    // Try alternate parsing - maybe it's a signed data structure
    throw new Error('Failed to parse PKCS#7 data');
  }
  
  return results;
}

// Parse PKCS#12 (PFX)
function parsePKCS12(base64Data: string, password: string): { 
  certificates: Array<{ details: CertificateDetails; pem: string }>; 
  hasPrivateKey: boolean;
} {
  // Decode base64 to binary
  const der = forge.util.decode64(base64Data.replace(/\s/g, ''));
  const asn1 = forge.asn1.fromDer(der);
  
  // Parse PKCS#12
  const p12 = forge.pkcs12.pkcs12FromAsn1(asn1, password);
  
  const certificates: Array<{ details: CertificateDetails; pem: string }> = [];
  let hasPrivateKey = false;
  
  // Get bags
  const certBags = p12.getBags({ bagType: forge.pki.oids.certBag });
  const keyBags = p12.getBags({ bagType: forge.pki.oids.pkcs8ShroudedKeyBag });
  
  // Check for private key
  const keyBagArray = keyBags[forge.pki.oids.pkcs8ShroudedKeyBag];
  if (keyBagArray && keyBagArray.length > 0) {
    hasPrivateKey = true;
  }
  
  // Also check for unencrypted key bag
  const unencryptedKeyBags = p12.getBags({ bagType: forge.pki.oids.keyBag });
  const unencryptedKeyBagArray = unencryptedKeyBags[forge.pki.oids.keyBag];
  if (unencryptedKeyBagArray && unencryptedKeyBagArray.length > 0) {
    hasPrivateKey = true;
  }
  
  // Extract certificates
  const certBagArray = certBags[forge.pki.oids.certBag];
  if (certBagArray) {
    for (const bag of certBagArray) {
      if (bag.cert) {
        try {
          const certPem = forge.pki.certificateToPem(bag.cert);
          const { details } = parseCertificate(certPem);
          certificates.push({ details, pem: certPem });
        } catch {
          // Skip invalid cert
        }
      }
    }
  }
  
  return { certificates, hasPrivateKey };
}

// Detect input type
function detectType(data: string): 'cert' | 'csr' | 'pkcs7' | 'pkcs12' {
  const trimmed = data.trim();
  
  if (trimmed.includes('-----BEGIN CERTIFICATE REQUEST-----') || 
      trimmed.includes('-----BEGIN NEW CERTIFICATE REQUEST-----')) {
    return 'csr';
  }
  
  if (trimmed.includes('-----BEGIN CERTIFICATE-----')) {
    return 'cert';
  }
  
  if (trimmed.includes('-----BEGIN PKCS7-----') || 
      trimmed.includes('-----BEGIN CMS-----')) {
    return 'pkcs7';
  }
  
  // Try to detect binary format from base64
  try {
    const decoded = forge.util.decode64(trimmed.replace(/\s/g, ''));
    const hex = forge.util.bytesToHex(decoded);
    
    // PKCS#12 OID: 1.2.840.113549.1.12 = 2a864886f70d010c
    if (hex.includes('2a864886f70d010c')) {
      return 'pkcs12';
    }
    
    // PKCS#7 Signed Data OID: 1.2.840.113549.1.7.2 = 2a864886f70d0107
    if (hex.includes('2a864886f70d0107')) {
      return 'pkcs7';
    }
  } catch {
    // Ignore
  }
  
  return 'cert'; // Default
}

// Normalize PEM format
function normalizePem(pem: string): string {
  const match = pem.match(/-----BEGIN CERTIFICATE-----([\s\S]+?)-----END CERTIFICATE-----/);
  if (match) {
    const base64 = match[1].replace(/\s/g, '');
    const lines = base64.match(/.{1,64}/g) || [];
    return `-----BEGIN CERTIFICATE-----\n${lines.join('\n')}\n-----END CERTIFICATE-----`;
  }
  return pem;
}

export function CertDecoder() {
  const [input, setInput] = useState('');
  const [password, setPassword] = useState('');
  const [result, setResult] = useState<DecodeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleDecode = useCallback(() => {
    if (!input.trim()) {
      setError('Please enter certificate or CSR data');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setCopiedIndex(null);

    // Use setTimeout for UI responsiveness
    setTimeout(() => {
      try {
        let cleanData = input.replace(/\\n/g, '\n').replace(/\\r/g, '').trim();
        const detectedType = detectType(cleanData);

        if (detectedType === 'csr') {
          // Add headers if missing
          if (!cleanData.includes('-----BEGIN')) {
            cleanData = `-----BEGIN CERTIFICATE REQUEST-----\n${cleanData}\n-----END CERTIFICATE REQUEST-----`;
          }
          const csrDetails = parseCSR(cleanData);
          setResult({
            type: 'csr',
            csr: csrDetails,
            timestamp: new Date().toISOString(),
          });
          return;
        }

        if (detectedType === 'pkcs7') {
          const certificates = parsePKCS7(cleanData);
          if (certificates.length === 0) {
            setError('No certificates found in PKCS#7 data');
            return;
          }
          setResult({
            type: 'pkcs7',
            certificates,
            count: certificates.length,
            timestamp: new Date().toISOString(),
          });
          return;
        }

        if (detectedType === 'pkcs12') {
          try {
            const { certificates, hasPrivateKey } = parsePKCS12(cleanData.replace(/\s/g, ''), password);
            if (certificates.length === 0) {
              setError('No certificates found in PKCS#12 file');
              return;
            }
            setResult({
              type: 'pkcs12',
              certificates,
              hasPrivateKey,
              count: certificates.length,
              timestamp: new Date().toISOString(),
            });
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            if (msg.includes('Invalid password') || msg.includes('PKCS#12 MAC')) {
              setError('Invalid password for PKCS#12 file');
            } else {
              setError('Failed to parse PKCS#12 file. Check the password.');
            }
          }
          return;
        }

        // Default: X.509 certificate
        // Add headers if missing
        if (!cleanData.includes('-----BEGIN')) {
          cleanData = `-----BEGIN CERTIFICATE-----\n${cleanData}\n-----END CERTIFICATE-----`;
        }

        const { details } = parseCertificate(cleanData);
        setResult({
          type: 'cert',
          certificate: details,
          pem: normalizePem(cleanData),
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to decode';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    }, 10);
  }, [input, password]);

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDaysLabel = (days: number) => {
    if (days <= 0) return 'Expired';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'cert': return 'X.509 Certificate';
      case 'csr': return 'Certificate Signing Request (CSR)';
      case 'pkcs7': return 'PKCS#7 / P7B Certificate Bundle';
      case 'pkcs12': return 'PKCS#12 / PFX Certificate';
      default: return type;
    }
  };

  const renderCertDetails = (cert: CertificateDetails, pem?: string, index: number = 0, showPem: boolean = true) => {
    const hasEKU = cert.extKeyUsage && cert.extKeyUsage.length > 0;
    const hasServerAuth = cert.extKeyUsage?.some(eku => eku.name === 'Server Authentication') ?? false;
    const hasClientAuth = cert.extKeyUsage?.some(eku => eku.name === 'Client Authentication') ?? false;
    
    return (
    <div key={index} className="space-y-4">
      {/* Extended Key Usage - always shown */}
      <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
        <h4 className="text-sm font-medium text-[var(--text-muted)] mb-3">
          Extended Key Usage
        </h4>
        {hasEKU ? (
          <>
            {/* Server/Client Auth Summary */}
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                hasServerAuth 
                  ? 'bg-green-500/10 border border-green-500/30 text-green-400' 
                  : 'bg-red-500/10 border border-red-500/30 text-red-400'
              }`}>
                {hasServerAuth ? (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Supports Server Authentication
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    No Server Authentication
                  </>
                )}
              </div>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                hasClientAuth 
                  ? 'bg-green-500/10 border border-green-500/30 text-green-400' 
                  : 'bg-orange-500/10 border border-orange-500/30 text-orange-500 dark:text-orange-400'
              }`}>
                {hasClientAuth ? (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Supports Client Authentication
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    No Client Authentication
                  </>
                )}
              </div>
            </div>
            {/* All EKU tags */}
            <div className="flex flex-wrap gap-2">
              {cert.extKeyUsage.map((eku, i) => (
                <span
                  key={i}
                  className="px-2 py-1 rounded bg-[var(--accent-dim)] border border-[var(--accent)]/30 text-xs text-[var(--accent)]"
                >
                  {eku.name}
                  <span className="ml-1.5 text-[var(--text-secondary)] font-mono text-xs">(OID: {eku.oid})</span>
                </span>
              ))}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-muted)]">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            No Extended Key Usage defined (typical for CA/Root certificates)
          </div>
        )}
      </div>

      {/* Subject */}
      <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
        <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2 flex items-center gap-2">
          <Server className="w-4 h-4" />
          Subject
        </h4>
        <div className="space-y-1 text-sm">
          {cert.subject.CN && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Common Name:</span>
              <span className="font-medium break-all">{cert.subject.CN}</span>
            </div>
          )}
          {cert.subject.O && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Organization:</span>
              <span>{cert.subject.O}</span>
            </div>
          )}
          {cert.subject.OU && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Org Unit:</span>
              <span>{cert.subject.OU}</span>
            </div>
          )}
          {(cert.subject.L || cert.subject.ST || cert.subject.C) && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Location:</span>
              <span>{[cert.subject.L, cert.subject.ST, cert.subject.C].filter(Boolean).join(', ')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Issuer */}
      <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
        <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2">Issuer</h4>
        <div className="space-y-1 text-sm">
          {cert.issuer.CN && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Common Name:</span>
              <span className="font-medium break-all">{cert.issuer.CN}</span>
            </div>
          )}
          {cert.issuer.O && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Organization:</span>
              <span>{cert.issuer.O}</span>
            </div>
          )}
        </div>
      </div>

      {/* Validity */}
      <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
        <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Validity
        </h4>
        <div className="space-y-1 text-sm">
          <div className="grid grid-cols-[100px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Not Before:</span>
            <span>{formatDate(cert.validFrom)}</span>
          </div>
          <div className="grid grid-cols-[100px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Not After:</span>
            <span className="flex items-center gap-2">
              {formatDate(cert.validTo)}
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                cert.daysRemaining <= 0
                  ? 'bg-red-500/20 text-red-600 dark:text-red-400'
                  : cert.daysRemaining <= 30
                    ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400'
                    : 'bg-green-500/20 text-green-600 dark:text-green-400'
              }`}>
                {getDaysLabel(cert.daysRemaining)}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Technical Details */}
      <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
        <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2 flex items-center gap-2">
          <Fingerprint className="w-4 h-4" />
          Technical Details
        </h4>
        <div className="space-y-1 text-sm">
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Serial Number:</span>
            <span className="font-mono text-xs break-all flex items-center gap-2">
              {cert.serialNumber}
              <button
                onClick={() => navigator.clipboard.writeText(cert.serialNumber)}
                className="p-1 hover:bg-[var(--bg-tertiary)] rounded transition-colors"
                title="Copy serial number"
              >
                <Copy className="w-3 h-3 text-[var(--text-muted)]" />
              </button>
            </span>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Signature Alg:</span>
            <span>{cert.signatureAlgorithm}</span>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Public Key:</span>
            <span>{cert.publicKeyAlgorithm}{cert.publicKeySize ? ` (${cert.publicKeySize} bit)` : ''}</span>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">SHA-1:</span>
            <span className="font-mono text-xs break-all">{cert.fingerprint}</span>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">SHA-256:</span>
            <span className="font-mono text-xs break-all">{cert.fingerprint256}</span>
          </div>
        </div>
      </div>

      {/* Key Usage */}
      {cert.keyUsage && cert.keyUsage.length > 0 && (
        <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
          <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2">
            Key Usage
          </h4>
          <div className="flex flex-wrap gap-2">
            {cert.keyUsage.map((usage, i) => (
              <span
                key={i}
                className="px-2 py-1 rounded bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-xs"
              >
                {usage}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Subject Alt Names */}
      {cert.subjectAltNames.length > 0 && (
        <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
          <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2">
            Subject Alternative Names ({cert.subjectAltNames.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {cert.subjectAltNames.slice(0, 15).map((san, i) => (
              <span
                key={i}
                className="px-2 py-1 rounded bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-xs font-mono"
              >
                {san}
              </span>
            ))}
            {cert.subjectAltNames.length > 15 && (
              <span className="px-2 py-1 text-xs text-[var(--text-muted)]">
                +{cert.subjectAltNames.length - 15} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* PEM Output with Copy Button */}
      {showPem && pem && (
        <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-[var(--text-muted)] flex items-center gap-2">
              <FileKey className="w-4 h-4" />
              PEM Certificate
            </h4>
            <button
              onClick={() => handleCopy(pem, index)}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-[var(--bg-tertiary)] hover:bg-[var(--accent-dim)] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
            >
              {copiedIndex === index ? (
                <>
                  <Check className="w-3 h-3" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  Copy PEM
                </>
              )}
            </button>
          </div>
          <pre className="font-mono text-xs overflow-x-auto p-3 bg-[var(--bg-tertiary)] rounded border border-[var(--border-color)] max-h-48 overflow-y-auto">
            {pem}
          </pre>
        </div>
      )}
    </div>
  );
  };

  const renderCSRDetails = (csr: CSRDetails) => (
    <div className="space-y-4">
      {/* Subject */}
      <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
        <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2 flex items-center gap-2">
          <Server className="w-4 h-4" />
          Subject
        </h4>
        <div className="space-y-1 text-sm">
          {csr.subject.CN && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Common Name:</span>
              <span className="font-medium break-all">{csr.subject.CN}</span>
            </div>
          )}
          {csr.subject.O && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Organization:</span>
              <span>{csr.subject.O}</span>
            </div>
          )}
          {csr.subject.OU && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Org Unit:</span>
              <span>{csr.subject.OU}</span>
            </div>
          )}
          {(csr.subject.L || csr.subject.ST || csr.subject.C) && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="text-[var(--text-muted)]">Location:</span>
              <span>{[csr.subject.L, csr.subject.ST, csr.subject.C].filter(Boolean).join(', ')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Subject Alt Names */}
      {csr.subjectAltNames && csr.subjectAltNames.length > 0 && (
        <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
          <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2">
            Subject Alternative Names ({csr.subjectAltNames.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {csr.subjectAltNames.slice(0, 15).map((san, i) => (
              <span
                key={i}
                className="px-2 py-1 rounded bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-xs font-mono"
              >
                {san}
              </span>
            ))}
            {csr.subjectAltNames.length > 15 && (
              <span className="px-2 py-1 text-xs text-[var(--text-muted)]">
                +{csr.subjectAltNames.length - 15} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Technical Details */}
      <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)]">
        <h4 className="text-sm font-medium text-[var(--text-muted)] mb-2 flex items-center gap-2">
          <Fingerprint className="w-4 h-4" />
          Technical Details
        </h4>
        <div className="space-y-1 text-sm">
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Public Key:</span>
            <span>{csr.publicKeyAlgorithm}{csr.publicKeySize ? ` (${csr.publicKeySize} bit)` : ''}</span>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span className="text-[var(--text-muted)]">Signature Alg:</span>
            <span>{csr.signatureAlgorithm}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOutput = () => {
    if (error) {
      return (
        <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
          <span className="font-semibold">Error:</span> {error}
        </div>
      );
    }

    if (!result) {
      return (
        <div className="text-[var(--text-muted)] text-center py-8">
          <p>Paste a certificate, CSR, PKCS#7, or PKCS#12 (base64) to decode its contents.</p>
          <p className="text-xs mt-2">Supports: PEM, DER (base64), PKCS#7/P7B, PKCS#12/PFX</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Type Badge */}
        <div className={`flex items-center gap-4 p-4 rounded-lg ${
          result.type === 'cert' && result.certificate && result.certificate.daysRemaining <= 0
            ? 'bg-red-500/20 border border-red-500/30'
            : 'bg-[var(--accent-dim)]'
        }`}>
          <Shield className={`w-10 h-10 ${
            result.type === 'cert' && result.certificate && result.certificate.daysRemaining <= 0
              ? 'text-red-500'
              : 'text-[var(--accent)]'
          }`} />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`font-semibold text-lg ${
                  result.type === 'cert' && result.certificate && result.certificate.daysRemaining <= 0
                    ? 'text-red-500'
                    : 'text-[var(--accent)]'
                }`}>
                  {getTypeLabel(result.type)}
                </span>
                {result.type === 'cert' && result.certificate && (
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    result.certificate.daysRemaining <= 0
                      ? 'bg-red-500/30 text-red-500 dark:text-red-400'
                      : result.certificate.daysRemaining <= 30
                        ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400'
                        : 'bg-green-500/20 text-green-600 dark:text-green-400'
                  }`}>
                    {getDaysLabel(result.certificate.daysRemaining)}
                  </span>
                )}
                {result.type === 'cert' && result.certificate && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/30">
                    {result.certificate.isCA 
                      ? (result.certificate.isSelfSigned ? 'Root Certificate' : 'Intermediate CA')
                      : result.certificate.isSelfSigned 
                        ? 'Self-Signed Certificate'
                        : 'Leaf / Domain Certificate'}
                  </span>
                )}
              </div>
            </div>
            {(result.type === 'pkcs7' || result.type === 'pkcs12') && (
              <div className="text-sm text-[var(--text-muted)] mt-1">
                {result.count} certificate{result.count !== 1 ? 's' : ''} found
                {result.type === 'pkcs12' && result.hasPrivateKey && (
                  <span className="ml-2 text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Contains private key
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* CSR Details */}
        {result.type === 'csr' && renderCSRDetails(result.csr)}

        {/* Single Certificate */}
        {result.type === 'cert' && renderCertDetails(result.certificate, result.pem, 0, true)}

        {/* Multiple Certificates (PKCS#7 or PKCS#12) */}
        {(result.type === 'pkcs7' || result.type === 'pkcs12') && (
          <div className="space-y-8">
            {result.certificates.map((cert, index) => (
              <div key={index}>
                <h3 className="text-[var(--accent)] font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Certificate {index + 1} of {result.count}
                  {cert.details.isCA && (
                    <span className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-400 ml-2">
                      CA
                    </span>
                  )}
                </h3>
                {renderCertDetails(cert.details, cert.pem, index, true)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Client-side badge */}
      <div className="flex items-center gap-2 text-xs text-[var(--accent)]">
        <ShieldCheck className="w-4 h-4" />
        <span>100% Client-Side — Your certificates never leave your browser</span>
      </div>

      {/* Input Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          Certificate / CSR / PKCS#7 / PKCS#12
        </label>
        <textarea
          className="w-full h-48 px-4 py-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-xs resize-none"
          placeholder="-----BEGIN CERTIFICATE-----
MIIDXTCCAkWgAwIBAgIJAJC1HiIAZAiUMA...
-----END CERTIFICATE-----

Or paste a CSR, PKCS#7, or base64-encoded PKCS#12"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
        />
      </div>

      {/* Password field for PKCS#12 */}
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-xs">
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            Password (for PKCS#12)
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            className="w-full px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-sm"
            placeholder="Optional"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] mt-6">
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
            className="rounded"
          />
          Show password
        </label>
      </div>

      <button
        type="button"
        onClick={handleDecode}
        disabled={isLoading || !input.trim()}
        className="btn-primary flex items-center gap-2 px-6"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Decoding...
          </>
        ) : (
          <>
            <FileSearch className="w-4 h-4" />
            Decode
          </>
        )}
      </button>

      {/* Output Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          Decoded Information
        </label>
        <div className="output-panel">
          {renderOutput()}
        </div>
      </div>

      <div className="text-xs text-[var(--text-muted)]">
        <span className="text-[var(--accent)]">Supported formats:</span> X.509 PEM/DER, CSR (PKCS#10), PKCS#7/P7B, PKCS#12/PFX (requires password)
      </div>
    </div>
  );
}
